import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UniversalPage, Card, StatCard, NotificationCard, UniversalEmptyState,
  Button, Input, Modal, StatusBadge, useToast,
  PeriodFilter,
  type PeriodPreset,
} from '@afrilink/ui';
import { inPeriod, useFilterParams } from '@/shared/hooks/useFilterParams';
import { ArrowLeft, Check, Undo2 } from 'lucide-react';
import {
  salesService,
  type CompanySplitCommissionRow,
  type PayoutStatus,
  type CommissionPayout,
  type PayoutMethod,
} from '@/domains/sales/services/salesService';

/**
 * SalesCompanyCommissionPage — /sales/companies/:id
 *
 * The commercial position for one sales company: what was billed, what
 * SightHost recovered as cost, and what each side earned, month by month.
 *
 * Lives under /sales rather than /admin deliberately. The two people who need
 * it are Sipho (super_admin) and Phologo (sales_manager at the house company,
 * with NO auth role at all) - and /admin is unreachable for the second. Putting
 * it here is what makes "the same screen" literally true rather than
 * approximately true.
 *
 * Access is the view's job, not this page's. v_sales_company_commission admits
 * full staff and house sales managers for every company, a partner
 * sales_manager for their own, and nobody else - so a user who should not see
 * these figures gets no rows. There is no role check here for the same reason
 * PartnerLinkCard has none: two copies of a rule means one of them drifts.
 *
 * Money is never faked. A failed load renders "-" and an explicit warning, not
 * R0 - on a page about who is owed what, a zero that actually means "we could
 * not load it" is the most damaging thing this screen could show.
 */

function zar(n: number): string {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(n);
}

function monthLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
}

/** Every R0 gets a reason. An unexplained zero reads as a bug or a cheat. */
const STATUS_COPY: Record<PayoutStatus, { label: string; tone: string }> = {
  flat:                { label: 'Flat rate',        tone: 'text-muted-foreground' },
  payable:             { label: 'Payable',          tone: 'text-success' },
  // Agreement 4.2.6(c): month 1 of the Commission Period is remunerated by the
  // Once-off, whoever won the customer — agent OR partner link.
  first_month_onceoff: { label: 'Month 1 — once-off',  tone: 'text-warning-dark' },
  term_expired:        { label: 'Term ended',       tone: 'text-muted-foreground' },
};

/**
 * Month-or-larger windows only. `last-30` and `custom` are day-grain and would
 * ask a question this month-grain data cannot answer.
 */
const MONTH_ALIGNED_PRESETS: PeriodPreset[] = [
  'all-time',
  'this-month',
  'last-month',
  'this-quarter',
  'last-quarter',
  'ytd',
  'last-year',
];

export default function SalesCompanyCommissionPage() {
  const { id = '' } = useParams<{ id: string }>();

  const companyQ = useQuery({
    queryKey: ['sales', 'company', id],
    enabled: !!id,
    queryFn: () => salesService.getCompany(id),
  });

  const rowsQ = useQuery({
    queryKey: ['sales', 'company-commission', id],
    enabled: !!id,
    queryFn: () => salesService.listCompanySplitCommission({ companyId: id }),
  });

  const payoutsQ = useQuery({
    queryKey: ['sales', 'company-payouts', id],
    enabled: !!id,
    queryFn: () => salesService.listPayouts(id),
  });

  const oversightQ = useQuery({
    queryKey: ['sales', 'oversight'],
    queryFn: () => salesService.isHouseOversight(),
  });

  const qc = useQueryClient();
  const [payingMonth, setPayingMonth] = useState<{ month: string; amount: number } | null>(null);
  const [voiding, setVoiding] = useState<CommissionPayout | null>(null);

  /** Live (non-voided) payout per month. */
  const paidByMonth = useMemo(() => {
    const m = new Map<string, CommissionPayout>();
    for (const p of payoutsQ.data ?? []) if (!p.voided_at) m.set(p.payout_month, p);
    return m;
  }, [payoutsQ.data]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['sales', 'company-payouts', id] });
  };

  const company = companyQ.data;
  const rows = useMemo(() => rowsQ.data ?? [], [rowsQ.data]);
  const failed = companyQ.error || rowsQ.error;
  const loading = companyQ.isLoading || rowsQ.isLoading;

  /**
   * Period filter over `commission_month`.
   *
   * AR-1 normally forbids filtering a DERIVED bucket rather than the raw event
   * date — but that warning is about `v_sales_agent_commission` and
   * `v_sales_company_recurring_commission`, which §4.14 records as bucketing a
   * timestamptz RAW. This page reads `v_sales_company_commission`, which §4.14
   * marks as already correct (`sast_date(p.paid_at)`), so its bucket is the
   * SAST month and filtering it is sound.
   *
   * MONTH-ALIGNED PRESETS ONLY. The rows are month-grain, so a day-grain preset
   * would lie: "last 30 days" against a `2026-08-01` key either swallows all of
   * August or none of it, depending on today's date, and neither answer is the
   * one asked. Offering only month-or-larger windows keeps the control honest
   * rather than making the data pretend to a precision it does not have.
   */
  const commissionFilters = useFilterParams({ defaultPeriod: 'all-time' });

  // Roll the customer-month grain up to months for display.
  const months = useMemo(() => {
    const by = new Map<string, { month: string; rows: CompanySplitCommissionRow[] }>();
    for (const r of rows) {
      if (!inPeriod(r.commission_month, commissionFilters.range)) continue;
      const b = by.get(r.commission_month) ?? { month: r.commission_month, rows: [] };
      b.rows.push(r);
      by.set(r.commission_month, b);
    }
    return Array.from(by.values()).sort((a, b) => b.month.localeCompare(a.month));
  }, [rows, commissionFilters.range]);

  const totals = useMemo(() => ({
    gross: rows.reduce((s, r) => s + Number(r.gross_amount), 0),
    cost: rows.reduce((s, r) => s + Number(r.cost_amount), 0),
    partner: rows.reduce((s, r) => s + Number(r.partner_amount), 0),
    sighthost: rows.reduce((s, r) => s + Number(r.sighthost_amount), 0),
    // Settled amounts as ACTUALLY PAID, not recomputed — that is the point of
    // the snapshot, and "still to pay" is only meaningful against real payments.
    paidOut: (payoutsQ.data ?? [])
      .filter((p) => !p.voided_at)
      .reduce((s, p) => s + Number(p.amount), 0),
  }), [rows, payoutsQ.data]);

  const isSplit = company?.commission_model === 'net_split';
  const money = (n: number) => (failed ? '—' : zar(n));

  return (
    <UniversalPage
      title={company?.name ?? 'Company'}
      description={
        company
          ? isSplit
            ? `${company.partner_split_pct}% of net after ${zar(company.cost_per_account_cents / 100)} per account, for ${company.term_months} months`
            : `Flat ${company.recurring_commission_pct}% of recurring revenue`
          : 'Commission position'
      }
    >
      <Link to="/sales/companies" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> All companies
      </Link>

      {failed && (
        <NotificationCard
          tone="danger"
          title="Could not load commission figures"
          body="The amounts below are not reliable. Reload before paying anything or quoting a number to the partner."
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Billed to customers" value={money(totals.gross)} />
        {isSplit && <StatCard label="Cost recovered first" value={money(totals.cost)} />}
        <StatCard label="Partner earns" value={money(totals.partner)} tone="success" />
        <StatCard
          label="Still to pay"
          value={money(Math.max(totals.partner - totals.paidOut, 0))}
          tone={totals.partner - totals.paidOut > 0 ? 'warning' : undefined}
        />
        <StatCard label="SightHost retains" value={money(totals.sighthost)} />
      </div>

      {!loading && !failed && months.length === 0 && (
        <Card className="mt-6">
          <UniversalEmptyState
            title="Nothing billed yet"
            description={
              isSplit
                ? 'The split starts at this company’s first paid recurring invoice. Nothing has been paid yet, so there is nothing to divide — this is a genuine zero, not a loading failure.'
                : 'No recurring revenue attributed to this company yet.'
            }
          />
        </Card>
      )}

      {rows.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <PeriodFilter
            value={commissionFilters.period}
            from={commissionFilters.from}
            to={commissionFilters.to}
            onChange={commissionFilters.setPeriod}
            presets={MONTH_ALIGNED_PRESETS}
            variant="menu"
            label="Commission month"
          />
          <span className="text-xs text-muted-foreground">
            {months.length} month{months.length === 1 ? '' : 's'} shown
          </span>
        </div>
      )}

      {months.map((m) => {
        const gross = m.rows.reduce((s, r) => s + Number(r.gross_amount), 0);
        const cost = m.rows.reduce((s, r) => s + Number(r.cost_amount), 0);
        const partner = m.rows.reduce((s, r) => s + Number(r.partner_amount), 0);
        const sighthost = m.rows.reduce((s, r) => s + Number(r.sighthost_amount), 0);
        const paid = paidByMonth.get(m.month);
        const canPay = oversightQ.data === true;
        return (
          <Card key={m.month} className="mt-6 p-4 sm:p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold">{monthLabel(m.month)}</h2>
                {paid ? (
                  <StatusBadge tone="active">
                    <Check className="h-3 w-3" /> Paid {zar(Number(paid.amount))}
                  </StatusBadge>
                ) : partner > 0 ? (
                  <StatusBadge tone="draft">Unpaid</StatusBadge>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {m.rows.length} customer{m.rows.length === 1 ? '' : 's'}
                </span>
                {canPay && !paid && partner > 0 && (
                  <Button size="sm" variant="primary"
                    onClick={() => setPayingMonth({ month: m.month, amount: partner })}>
                    Record payout
                  </Button>
                )}
                {canPay && paid && (
                  <Button size="sm" variant="ghost" onClick={() => setVoiding(paid)}
                    title="Void this payout — it stays on the record with your reason">
                    <Undo2 className="h-4 w-4" /> Void
                  </Button>
                )}
              </div>
            </div>

            {/* The engine is live; the payout is frozen. If a refund lands after
                settlement these two legitimately differ, and saying so is the
                whole point of storing a snapshot. */}
            {paid && Number(paid.amount) !== partner && (
              <p className="mt-2 text-xs text-warning-dark">
                Paid {zar(Number(paid.amount))}, but this month now calculates at {zar(partner)} —
                the figures moved after settlement. The paid amount stands; reconcile before
                adjusting anything.
              </p>
            )}

            {/* The month's arithmetic, shown as arithmetic. A partner querying a
                payment should be able to follow it without a spreadsheet. */}
            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span>{zar(gross)} billed</span>
              {isSplit && <><span className="text-muted-foreground">less</span><span>{zar(cost)} cost</span></>}
              <span className="text-muted-foreground">=</span>
              <span className="font-medium">{zar(gross - cost)} to divide</span>
              <span className="text-muted-foreground">&rarr;</span>
              <span className="font-semibold text-success">{zar(partner)} partner</span>
              <span className="text-muted-foreground">/</span>
              <span className="font-medium">{zar(sighthost)} SightHost</span>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[36rem] text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Month of term</th>
                    <th className="py-2 pr-3 font-medium">Accounts</th>
                    <th className="py-2 pr-3 text-right font-medium">Billed</th>
                    {isSplit && <th className="py-2 pr-3 text-right font-medium">Cost</th>}
                    <th className="py-2 pr-3 text-right font-medium">Partner</th>
                    <th className="py-2 pr-3 text-right font-medium">SightHost</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {m.rows.map((r) => {
                    const s = STATUS_COPY[r.payout_status];
                    return (
                      <tr key={`${r.customer_id}-${r.commission_month}`} className="border-b last:border-0">
                        <td className="py-2 pr-3">
                          {r.term_months ? `${r.term_month_index} of ${r.term_months}` : '—'}
                        </td>
                        <td className="py-2 pr-3">{r.cpanel_accounts}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{zar(Number(r.gross_amount))}</td>
                        {isSplit && (
                          <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                            {zar(Number(r.cost_amount))}
                          </td>
                        )}
                        <td className="py-2 pr-3 text-right font-medium tabular-nums">
                          {zar(Number(r.partner_amount))}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">{zar(Number(r.sighthost_amount))}</td>
                        <td className={`py-2 text-xs ${s.tone}`}>{s.label}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}

      {isSplit && !failed && months.length > 0 && (
        <p className="mt-4 text-xs text-muted-foreground">
          Figures follow the agreement as recorded in the system. The signed agreement governs
          what is actually payable.
        </p>
      )}

      {payingMonth && (
        <RecordPayoutModal
          companyId={id}
          companyName={company?.name ?? 'this partner'}
          month={payingMonth.month}
          expected={payingMonth.amount}
          onClose={() => setPayingMonth(null)}
          onDone={() => { setPayingMonth(null); refresh(); }}
        />
      )}

      {voiding && (
        <VoidPayoutModal
          payout={voiding}
          onClose={() => setVoiding(null)}
          onDone={() => { setVoiding(null); refresh(); }}
        />
      )}
    </UniversalPage>
  );
}

/**
 * Records that a payment was MADE, outside the system (AD-16 — money moves by
 * hand). The amount is deliberately not an input: the RPC derives it from the
 * engine, so a typo cannot enter the ledger as fact. It is shown, not editable.
 */
function RecordPayoutModal({
  companyId, companyName, month, expected, onClose, onDone,
}: {
  companyId: string; companyName: string; month: string; expected: number;
  onClose: () => void; onDone: () => void;
}) {
  const { toast } = useToast();
  const [method, setMethod] = useState<PayoutMethod>('eft');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setError(null);
    try {
      await salesService.recordPayout({ companyId, month, method, reference, notes });
      toast({ tone: 'success', title: 'Payout recorded', body: `${monthLabel(month)} marked as paid.` });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Record payout — ${monthLabel(month)}`}>
      <p className="text-sm text-muted-foreground">
        Confirms that {companyName} has been paid for {monthLabel(month)}. This records the
        payment; it does not send any money.
      </p>

      <div className="mt-4 rounded-md border p-3">
        <div className="text-xs uppercase text-muted-foreground">Amount</div>
        <div className="text-xl font-semibold tabular-nums">{zar(expected)}</div>
        <p className="mt-1 text-xs text-muted-foreground">
          Calculated from the commission engine and frozen once recorded, so a later refund
          cannot restate a month you have already paid.
        </p>
      </div>

      <label className="mt-4 block text-sm font-medium">How was it paid?</label>
      <select
        className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
        value={method}
        onChange={(e) => setMethod(e.target.value as PayoutMethod)}
      >
        <option value="eft">EFT / bank transfer</option>
        <option value="payfast">PayFast</option>
        <option value="offset">Offset against amounts owed</option>
        <option value="other">Other</option>
      </select>

      <label className="mt-3 block text-sm font-medium">Reference</label>
      <Input value={reference} onChange={(e) => setReference(e.target.value)}
             placeholder="Bank reference or proof of payment" />

      <label className="mt-3 block text-sm font-medium">Notes (optional)</label>
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} />

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>
          {busy ? 'Recording…' : 'Mark as paid'}
        </Button>
      </div>
    </Modal>
  );
}

/** Voids rather than edits or deletes — a payout recorded in error is part of
 *  the record, and the reason matters more than a tidy table. */
function VoidPayoutModal({
  payout, onClose, onDone,
}: { payout: CommissionPayout; onClose: () => void; onDone: () => void }) {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setError(null);
    try {
      await salesService.voidPayout(payout.id, reason);
      toast({ tone: 'success', title: 'Payout voided', body: 'The month is payable again.' });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Void payout — ${monthLabel(payout.payout_month)}`}>
      <p className="text-sm text-muted-foreground">
        Voids the {zar(Number(payout.amount))} payout recorded for{' '}
        {monthLabel(payout.payout_month)}. The entry stays on the record with your reason, and
        the month becomes payable again.
      </p>

      <label className="mt-4 block text-sm font-medium">Why is this being voided?</label>
      <Input value={reason} onChange={(e) => setReason(e.target.value)}
             placeholder="e.g. recorded against the wrong month" />

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="destructive" onClick={submit} disabled={busy || !reason.trim()}>
          {busy ? 'Voiding…' : 'Void payout'}
        </Button>
      </div>
    </Modal>
  );
}
